import type { ModelKey } from "./model.type";
import type { UserModelActionType } from "@/entities/action";
import type { Int } from "@/types";

import { MODEL_CONFIG_MAPPER } from "./model.constant";

export const isModelKey = (modelKey?: string): modelKey is ModelKey =>
  !!(modelKey && modelKey in MODEL_CONFIG_MAPPER);

export const extractModelFromPath = (path: string) =>
  path.split("\\")?.pop()?.toLowerCase();

export const getModelKey = (modelKey?: ModelKey | string) => {
  if (!modelKey) return undefined;

  if (isModelKey(modelKey)) return modelKey;

  // Extract modelKey from api class path
  const modelKeyExtracted = extractModelFromPath(modelKey);

  if (isModelKey(modelKeyExtracted)) return modelKeyExtracted;

  return undefined;
};

export const getModelRoute = (modelKey: ModelKey | string) => {
  const model = getModelKey(modelKey);

  if (!model) return undefined;

  return MODEL_CONFIG_MAPPER[model]?.route ?? undefined;
};

export const getModelLink = (modelKey: ModelKey, modelId?: Int) => {
  if (!modelId) return undefined;

  const route = getModelRoute(modelKey);

  if (!route) return undefined;

  return `${route}/${modelId}`;
};

export const getModelApiKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.apiKey ?? model;
};

export const getModelQueryKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.queryKey ?? model;
};

export const getModelAuthorizationKeys = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.authorizationKeys ?? {};
};

export const getModelAuthorizationKey = (
  model: ModelKey,
  action: UserModelActionType
) => {
  return getModelAuthorizationKeys(model)?.[action];
};

export const getModelTranslationKey = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.translationKey ?? model;
};

export const isModelDeleteAsync = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.deleteAsync ?? false;
};

export const isModelRestoreSupported = (model: ModelKey) => {
  return MODEL_CONFIG_MAPPER[model]?.restore ?? false;
};
